import React from 'react';
import PropTypes from 'prop-types';
import { LuChartColumn, LuChartLine, LuChartPie, LuCircleDollarSign } from 'react-icons/lu';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import GlassIcon from '../../../components/ui/GlassIcon';
import useBudgetText from '../budgetText';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const CARD = 'tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-[1.5rem] tw-border tw-border-white/80 tw-bg-white/80 tw-p-4 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_16px_40px_-30px_rgba(15,23,42,0.35)] sm:tw-p-5 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_16px_40px_-30px_rgba(0,0,0,0.9)]';

const ChartCard = ({ icon: Icon, title, children }) => (
  <section className={CARD}>
    <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
      <span className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      {title}
    </h3>
    <div className="tw-relative tw-h-[300px] tw-w-full">{children}</div>
  </section>
);

ChartCard.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node };

/**
 * İstatistikler: income vs expense totals, payment states, categories and the
 * monthly trend over every record (the old calculations; labels translated,
 * stored category names shown as they are).
 */
const BudgetCharts = React.memo(({ budgets, isDarkMode }) => {
  const { label, statusLabel } = useBudgetText();

  // Calculate data for charts
  const calculateChartData = () => {
    const incomeData = budgets.filter(b => b['GiderTipi'] === 'Gelir');
    const expenseData = budgets.filter(b => b['GiderTipi'] !== 'Gelir');

    // Income vs Expense Bar Chart
    const totalIncome = incomeData.reduce((sum, item) => sum + parseFloat(item['BütçeMiktarı'] || 0), 0);
    const totalExpense = expenseData.reduce((sum, item) => sum + parseFloat(item['BütçeMiktarı'] || 0), 0);

    // Payment Status Distribution
    const paymentStatusData = {
      'Alındı': incomeData.filter(i => i['ÖdemeDurumu'] === 'Alındı').length,
      'Beklemede': incomeData.filter(i => i['ÖdemeDurumu'] === 'Beklemede').length,
      'Ödendi': expenseData.filter(e => e['ÖdemeDurumu'] === 'Ödendi').length,
      'Bekleyen': expenseData.filter(e => e['ÖdemeDurumu'] === 'Beklemede').length
    };

    // Category Distribution
    const categoryData = {};
    [...incomeData, ...expenseData].forEach(item => {
      const category = item['BütçeKategorisi'] || label('charts.other');
      categoryData[category] = (categoryData[category] || 0) + 1;
    });

    // Monthly Trend
    const monthlyData = {};
    [...incomeData, ...expenseData].forEach(item => {
      const date = new Date(item['BütçeTarihi']);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = { income: 0, expense: 0 };
      }
      if (item['GiderTipi'] === 'Gelir') {
        monthlyData[monthYear].income += parseFloat(item['BütçeMiktarı'] || 0);
      } else {
        monthlyData[monthYear].expense += parseFloat(item['BütçeMiktarı'] || 0);
      }
    });

    return {
      incomeExpenseData: {
        labels: [label('types.income'), label('types.expense')],
        datasets: [{
          label: label('charts.totalAmount'),
          data: [totalIncome, totalExpense],
          backgroundColor: ['#38B2AC', '#E53E3E'],
          borderColor: ['#2C7A7B', '#C53030'],
          borderWidth: 1
        }]
      },
      paymentStatusData: {
        labels: Object.keys(paymentStatusData).map(statusLabel),
        datasets: [{
          data: Object.values(paymentStatusData),
          backgroundColor: ['#38A169', '#ECC94B', '#4299E1', '#ED8936'],
          borderColor: ['#2F855A', '#D69E2E', '#3182CE', '#DD6B20'],
          borderWidth: 1
        }]
      },
      categoryData: {
        labels: Object.keys(categoryData),
        datasets: [{
          data: Object.values(categoryData),
          backgroundColor: [
            '#4299E1', '#48BB78', '#ECC94B', '#ED8936', '#F56565',
            '#9F7AEA', '#4FD1C5', '#F6AD55', '#FC8181', '#B794F4'
          ],
          borderWidth: 1
        }]
      },
      monthlyTrendData: {
        labels: Object.keys(monthlyData),
        datasets: [
          {
            label: label('types.income'),
            data: Object.values(monthlyData).map(d => d.income),
            borderColor: '#38B2AC',
            backgroundColor: 'rgba(56, 178, 172, 0.1)',
            tension: 0.4
          },
          {
            label: label('types.expense'),
            data: Object.values(monthlyData).map(d => d.expense),
            borderColor: '#E53E3E',
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            tension: 0.4
          }
        ]
      }
    };
  };

  const chartData = calculateChartData();

  const textColor = isDarkMode ? '#E2E8F0' : '#4A5568';
  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const legend = { position: 'top', labels: { color: textColor } };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend },
    scales: {
      y: { beginAtZero: true, ticks: { color: textColor }, grid: { color: gridColor } },
      x: { ticks: { color: textColor }, grid: { color: gridColor } },
    },
  };
  // Pies draw no axes behind them
  const pieOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend } };

  return (
    <section aria-labelledby="budget-charts-title" className="tw-space-y-3">
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-1">
        <GlassIcon gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30" size="xs">
          <LuChartColumn aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <div className="tw-min-w-0">
          <h2 id="budget-charts-title" className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{label('charts.title')}</h2>
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('charts.subtitle')}</p>
        </div>
      </div>
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
        <ChartCard icon={LuCircleDollarSign} title={label('charts.incomeExpense')}>
          <Bar data={chartData.incomeExpenseData} options={chartOptions} />
        </ChartCard>
        <ChartCard icon={LuChartPie} title={label('charts.paymentStatus')}>
          <Pie data={chartData.paymentStatusData} options={pieOptions} />
        </ChartCard>
        <ChartCard icon={LuChartPie} title={label('charts.category')}>
          <Pie data={chartData.categoryData} options={pieOptions} />
        </ChartCard>
        <ChartCard icon={LuChartLine} title={label('charts.monthly')}>
          <Line data={chartData.monthlyTrendData} options={chartOptions} />
        </ChartCard>
      </div>
    </section>
  );
});

BudgetCharts.displayName = 'BudgetCharts';

BudgetCharts.propTypes = {
  budgets: PropTypes.arrayOf(PropTypes.object).isRequired,
  isDarkMode: PropTypes.bool,
};

export default BudgetCharts;
